import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Box, Typography, Paper, TextField, Button, Divider, Alert,
  CircularProgress, Avatar, Chip, Switch, FormControlLabel,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
import packageJson from '../../package.json';

const APP_VERSION = packageJson.version;

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: '"Outfit", sans-serif',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 2 },
  },
  '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', color: NAV_BG },
};

export default function DriverSettingsPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const [profile, setProfile] = useState(null);
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const [proximityAlertEnabled, setProximityAlertEnabled] = useState(true);
  const [proximityAlertSoundEnabled, setProximityAlertSoundEnabled] = useState(true);
  const [routeAbsencePopupEnabled, setRouteAbsencePopupEnabled] = useState(true);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  useEffect(() => {
    fetch(backendUrl + 'api/driver-profile/', { headers: authHeader })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setProfile(data);
          setPhoneNumber(data.phone_number || '');
          setProximityAlertEnabled(data.proximityAlert_enabled !== false);
          setProximityAlertSoundEnabled(data.proximityAlertSound_enabled !== false);
          setRouteAbsencePopupEnabled(data.routeAbsencePopup_enabled !== false);
        } else {
          setProfileLoadError(true);
        }
      })
      .catch(() => setProfileLoadError(true));
  }, []);

  const handleSavePhone = async () => {
    setSavingPhone(true);
    setPhoneError('');
    setPhoneSaved(false);
    try {
      const res = await fetch(backendUrl + 'api/driver-profile/', {
        method: 'PATCH',
        headers: authHeader,
        body: JSON.stringify({ phone_number: phoneNumber }),
      });
      if (res.ok) {
        setPhoneSaved(true);
        setTimeout(() => setPhoneSaved(false), 3000);
      } else {
        setPhoneError(t('driver_settings.save_failed'));
      }
    } catch {
      setPhoneError(t('common.network_error'));
    }
    setSavingPhone(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (newPassword !== confirmPassword) { setPwError(t('preferences.password_mismatch')); return; }
    if (newPassword.length < 6) { setPwError(t('driver_settings.password_min_length')); return; }
    setPwLoading(true);
    try {
      const res = await fetch(backendUrl + 'api/driver/change-password/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwSuccess(t('preferences.password_saved'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwError(data.error || t('driver_settings.password_change_failed'));
      }
    } catch {
      setPwError(t('common.network_error'));
    }
    setPwLoading(false);
  };

  const toggleProximityAlert = async (e) => {
    const value = e.target.checked;
    setProximityAlertEnabled(value);
    try {
      await fetch(backendUrl + 'api/driver-profile/', {
        method: 'PATCH',
        headers: authHeader,
        body: JSON.stringify({ proximityAlert_enabled: value }),
      });
    } catch {
      setProximityAlertEnabled(!value);
    }
  };

  const toggleProximityAlertSound = async (e) => {
    const value = e.target.checked;
    setProximityAlertSoundEnabled(value);
    try {
      await fetch(backendUrl + 'api/driver-profile/', {
        method: 'PATCH',
        headers: authHeader,
        body: JSON.stringify({ proximityAlertSound_enabled: value }),
      });
    } catch {
      setProximityAlertSoundEnabled(!value);
    }
  };

  const toggleRouteAbsencePopup = async (e) => {
    const value = e.target.checked;
    setRouteAbsencePopupEnabled(value);
    try {
      await fetch(backendUrl + 'api/driver-profile/', {
        method: 'PATCH',
        headers: authHeader,
        body: JSON.stringify({ routeAbsencePopup_enabled: value }),
      });
    } catch {
      setRouteAbsencePopupEnabled(!value);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'el' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('sb_language', newLang);
    fetch(backendUrl + 'api/driver-profile/', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access },
      body: JSON.stringify({ lang_pref: newLang }),
    }).catch(() => {});
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG, mb: 0.5 }}>
        {t('nav.driver_settings')}
      </Typography>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 3 }}>
        {t('driver_settings.subtitle')}
      </Typography>
      {profileLoadError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontFamily: '"Outfit", sans-serif' }}>
          {t('common.network_error')}
        </Alert>
      )}

      {/* Profile card */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 52, height: 52, background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem' }}>
            {profile ? `${profile.firstname?.[0] || ''}${profile.lastname?.[0] || ''}` : <PersonIcon />}
          </Avatar>
          <Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1rem' }}>
              {profile ? `${profile.firstname} ${profile.lastname}` : '...'}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontSize: '0.85rem' }}>
              {profile?.school_name || ''}
            </Typography>
            <Chip size="small" label={t('driver_settings.role_driver')} sx={{ mt: 0.5, background: '#dbeafe', color: '#1d4ed8', fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem' }} />
          </Box>
        </Box>
      </Paper>

      {/* Phone number */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 2 }}>
          {t('preferences.phone_number')}
        </Typography>
        {phoneSaved && <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>{t('preferences.saved')}</Alert>}
        {phoneError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{phoneError}</Alert>}
        <TextField
          fullWidth size="small"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value.replace(/[^0-9+\- ]/g, ''))}
          placeholder={t('preferences.phone_number')}
          sx={inputSx}
        />
        <Button
          variant="contained" disableElevation onClick={handleSavePhone}
          disabled={savingPhone}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
            textTransform: 'none', borderRadius: 1.5, px: 3,
          }}
        >
          {savingPhone ? <CircularProgress size={16} sx={{ color: NAV_BG }} /> : t('preferences.save')}
        </Button>
      </Paper>

      {/* Change password */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 2 }}>
          {t('preferences.change_password')}
        </Typography>
        {pwSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>{pwSuccess}</Alert>}
        {pwError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{pwError}</Alert>}

        <TextField
          fullWidth size="small" type={showCurrent ? 'text' : 'password'}
          placeholder={t('driver_settings.current_password')}
          value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
          sx={inputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrent(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8' }}>
                  {showCurrent ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth size="small" type={showNew ? 'text' : 'password'}
          placeholder={t('preferences.new_password')}
          value={newPassword} onChange={e => setNewPassword(e.target.value)}
          sx={inputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8' }}>
                  {showNew ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth size="small" type="password"
          placeholder={t('preferences.confirm_password')}
          value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
        />
        <Button
          variant="contained" disableElevation onClick={handleChangePassword}
          disabled={pwLoading || !currentPassword || !newPassword || !confirmPassword}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
            textTransform: 'none', borderRadius: 1.5, px: 3,
          }}
        >
          {pwLoading ? <CircularProgress size={16} sx={{ color: NAV_BG }} /> : t('preferences.update_password')}
        </Button>
      </Paper>

      {/* Proximity alert */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <FormControlLabel
          control={<Switch checked={proximityAlertEnabled} onChange={toggleProximityAlert} />}
          label={
            <Box>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                {t('driver_settings.proximity_alert_label')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontSize: '0.85rem' }}>
                {t('driver_settings.proximity_alert_desc')}
              </Typography>
            </Box>
          }
        />
        <Divider sx={{ my: 1.5 }} />
        <FormControlLabel
          control={<Switch checked={proximityAlertSoundEnabled} onChange={toggleProximityAlertSound} />}
          label={
            <Box>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                {t('driver_settings.proximity_alert_sound_label')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontSize: '0.85rem' }}>
                {t('driver_settings.proximity_alert_sound_desc')}
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Absent children popup */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <FormControlLabel
          control={<Switch checked={routeAbsencePopupEnabled} onChange={toggleRouteAbsencePopup} />}
          label={
            <Box>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                {t('driver_settings.route_absence_popup_label')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontSize: '0.85rem' }}>
                {t('driver_settings.route_absence_popup_desc')}
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Language */}
      <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 1.5 }}>
          Language / Γλώσσα
        </Typography>
        <Button
          variant="outlined" onClick={toggleLanguage}
          sx={{
            fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none',
            borderRadius: 1.5, borderColor: AMBER, color: AMBER_DARK,
            '&:hover': { background: 'rgba(245,158,11,0.06)', borderColor: AMBER },
          }}
        >
          {i18n.language === 'en' ? 'Switch to Greek (ΕΛ)' : 'Switch to English (EN)'}
        </Button>
      </Paper>

      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', mt: 3 }}>
        {t('common.app_version', { version: APP_VERSION })}
      </Typography>
    </Box>
  );
}
